import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiUserCheck, FiUserX, FiLock } from 'react-icons/fi';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../services/users';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import UserForm from '../components/Users/UserForm';
import ResetPasswordModal from '../components/Users/ResetPasswordModal';
import toast from 'react-hot-toast';

const roleColors = {
  super_admin: 'bg-purple-100 text-purple-800',
  operations_manager: 'bg-blue-100 text-blue-800',
  drivers_manager: 'bg-green-100 text-green-800',
  companies_manager: 'bg-orange-100 text-orange-800',
  affiliates_manager: 'bg-yellow-100 text-yellow-800',
  tour_guides_manager: 'bg-indigo-100 text-indigo-800',
  blog_manager: 'bg-pink-100 text-pink-800',
  viewer: 'bg-gray-100 text-gray-800',
};

const roleLabels = {
  super_admin: 'Super Admin',
  operations_manager: 'Operations Manager',
  drivers_manager: 'Drivers Manager',
  companies_manager: 'Tour Companies Manager',
  affiliates_manager: 'Affiliates Manager',
  tour_guides_manager: 'Tour Guides Manager',
  blog_manager: 'Blog Manager',
  viewer: 'Viewer',
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({ role: '', status: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers(filters);
      setUsers(data.users || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (userData) => {
    try {
      await createUser(userData);
      toast.success('User created successfully');
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdate = async (id, userData) => {
    try {
      await updateUser(id, userData);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await resetUserPassword(userId, newPassword);
      toast.success('Password reset successfully');
      setShowResetModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Users Management</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Role</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="super_admin">Super Admin</option>
              <option value="operations_manager">Operations Manager</option>
              <option value="drivers_manager">Drivers Manager</option>
              <option value="companies_manager">Tour Companies Manager</option>
              <option value="affiliates_manager">Affiliates Manager</option>
              <option value="tour_guides_manager">Tour Guides Manager</option>
              <option value="blog_manager">Blog Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Username or email"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{user.username}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.full_name && (
                          <p className="text-xs text-gray-400">{user.full_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${roleColors[user.role]}`}>
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_active ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <FiUserCheck className="mr-1" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 text-sm">
                          <FiUserX className="mr-1" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {user.last_login ? formatDate(user.last_login) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowResetModal(true);
                        }}
                        className="text-orange-500 hover:text-orange-700 mr-3"
                        title="Reset Password"
                      >
                        <FiLock className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingUser(user)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                        title="Edit User"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user._id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete User"
                        disabled={user.role === 'super_admin'}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">No users found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* User Form Modal */}
      <UserForm
        isOpen={showForm || !!editingUser}
        onClose={() => {
          setShowForm(false);
          setEditingUser(null);
        }}
        onSubmit={editingUser ? (data) => handleUpdate(editingUser._id, data) : handleCreate}
        initialData={editingUser}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setSelectedUser(null);
        }}
        onSubmit={(newPassword) => handleResetPassword(selectedUser?._id, newPassword)}
        user={selectedUser}
      />
    </div>
  );
}
